import { ChatChannelIds, ChatMessageRelationType, ChatMessage as ChatMessageType, MAX_CHAT_MESSAGE_LIMIT, evaluateChatLinks } from '@core/types';
import { Button, Close } from '@core/ui';
import { chatRejectionHandled, forwardChatMessage, replyChatMessage, sendChatMessage, useAppDispatch, useAppSelector } from '@online-webinar/store';
import { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatMessageButton, ChatMessageErrorLabel, ChatMessageLimitExceedLabel, ChatMessageTextInput, MessageText } from '../../atoms';
import { MessageInfo } from '../MessageInfo';
import { RelatedMessageContent } from '../RelatedMessageContent';
import { SendMessageInput } from '../SendMessageInput';
import { StandardMessages } from '../StandardMessages';
import { UpdateNickname } from '../UpdateNickname';

export const ChatChannelSendMessage = ({
    channelId,
    relatedMessage: relatedMessageInput,
    relationType: relationTypeInput
}: {
    channelId: string;
    relatedMessage: ChatMessageType | null;
    relationType: ChatMessageRelationType | null;
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [maxCharMessageReached, setMaxCharMessageReached] = useState(false);
    const [canSendMessage, setCanSendMessage] = useState(false);
    const [messageToSend, setMessageToSend] = useState('');
    const [relatedMessage, setRelatedMessage] = useState<ChatMessageType | null>(null);
    const [relationType, setRelationType] = useState<ChatMessageRelationType | null>(null);

    const me = useAppSelector((state) => state.liveWebinar.me);
    const policy = useAppSelector((state) => state.chatLinkPolicy);
    const rejection = useAppSelector((state) => state.liveWebinar.lastChatRejection);
    const chatChannels = useAppSelector((state) => state.liveWebinar.chatChannels);
    const [linkError, setLinkError] = useState<'Chat.Message.LinkNotAllowed' | 'Chat.Message.TooManyLinks' | null>(null);

    // Restore a refused send (text + the message it replied to / forwarded) for the channel it belongs to.
    // This component is a single instance reused across every channel tab (no `key` on tab switch), so a
    // rejection is consumed unconditionally the moment it is seen — otherwise a rejection for a channel that
    // is no longer in view would sit in the store and replay a stale draft/error when that tab comes back.
    useEffect(() => {
        if (!rejection) return;
        if (rejection.channelId === channelId) {
            setMessageToSend(rejection.message);
            if (rejection.relatedMessageId && rejection.relationType) {
                const related = Object.values(chatChannels)
                    .flatMap((channel) => channel.messages)
                    .find((message) => message.messageId === rejection.relatedMessageId);
                if (related) {
                    setRelatedMessage(related);
                    setRelationType(rejection.relationType);
                }
            }
            setLinkError(rejection.errorCode === 'ChatLinkNotAllowedException' ? 'Chat.Message.LinkNotAllowed' : null);
        }
        dispatch(chatRejectionHandled());
    }, [rejection, channelId, chatChannels, dispatch]);

    useEffect(() => {
        if (relatedMessageInput && relationTypeInput) {
            setRelatedMessage(relatedMessageInput);
            setRelationType(relationTypeInput);
        }
    }, [relatedMessageInput, relationTypeInput]);

    useEffect(() => {
        if (messageToSend?.length > MAX_CHAT_MESSAGE_LIMIT) {
            setCanSendMessage(false);
            return;
        }

        if (!relatedMessage || relationType === ChatMessageRelationType.reply) {
            setCanSendMessage(messageToSend.length > 0);
            return;
        }

        setCanSendMessage(relationType === ChatMessageRelationType.forward);
    }, [messageToSend, relatedMessage, relationType]);

    if (ChatChannelIds.allChannel === channelId) {
        return null;
    }

    const onChatInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        if (MAX_CHAT_MESSAGE_LIMIT - e.target.value.length > 20) {
            setMaxCharMessageReached(false);
        } else if (MAX_CHAT_MESSAGE_LIMIT - e.target.value.length <= 20) {
            setMaxCharMessageReached(true);
        }
        setMessageToSend(e.target.value);
        setLinkError(null);
    };

    const handleStandardMessage = (text: string) => {
        setMessageToSend(text);
    };

    const resetRelatedMessage = () => {
        setRelatedMessage(null);
        setRelationType(null);
    };

    const sendMessage = () => {
        if (messageToSend?.length > MAX_CHAT_MESSAGE_LIMIT) {
            return;
        }

        const verdict = evaluateChatLinks([messageToSend, relatedMessage?.message], policy);
        if (verdict === 'blocked' || verdict === 'invalid') {
            setLinkError('Chat.Message.LinkNotAllowed');
            return;
        }
        if (verdict === 'tooMany') {
            setLinkError('Chat.Message.TooManyLinks');
            return;
        }

        if (relationType === ChatMessageRelationType.reply) {
            if (!relatedMessage) {
                resetRelatedMessage();
                return;
            }

            if (!messageToSend) {
                return;
            }

            dispatch(replyChatMessage({ message: messageToSend, channelId, relatedMessageId: relatedMessage.messageId }));
        } else if (relationType === ChatMessageRelationType.forward) {
            if (!relatedMessage) {
                resetRelatedMessage();
                return;
            }

            dispatch(forwardChatMessage({ message: messageToSend, channelId, relatedMessageId: relatedMessage.messageId }));
        } else {
            if (!messageToSend) {
                return;
            }

            dispatch(sendChatMessage({ message: messageToSend, channelId }));
        }

        resetRelatedMessage();
        setMessageToSend('');
        setMaxCharMessageReached(false);
        setLinkError(null);
    };

    const onChatKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault(); // Prevents the newline character from being added
            sendMessage();
        }
    };

    return (
        <>
            <div className="col-start-1 row-start-2 flex items-end overflow-hidden pointer-events-none z-1">
                <StandardMessages onSelectMessage={handleStandardMessage} />
            </div>
            {relatedMessage && (
                <div className={`row-start-3 ${me?.participantId === relatedMessage.senderParticipantId ? 'bg-chat-own-msg' : 'bg-gray-200'}`}>
                    <div className="w-full max-w-2xl flex items-center justify-between gap-2 mx-auto px-6 py-2">
                        <RelatedMessageContent>
                            <MessageInfo username={relatedMessage.senderNickname} time={relatedMessage.timestamp} />
                            <MessageText messageBody={relatedMessage.message} />
                        </RelatedMessageContent>
                        <Button type="button" onClick={resetRelatedMessage} iconbtn classNames="">
                            <Close classNames="size-6 stroke-warning" />
                            <span className="sr-only">{t('Chat.Message.Button.CancelForward')}</span>
                        </Button>
                    </div>
                </div>
            )}
            {me?.nickname && (
                <div className="row-start-4 bg-accent-lighter z-20">
                    <SendMessageInput>
                        {maxCharMessageReached && <ChatMessageLimitExceedLabel messageSize={MAX_CHAT_MESSAGE_LIMIT - messageToSend.length} />}
                        {linkError && <ChatMessageErrorLabel messageKey={linkError} />}
                        <ChatMessageTextInput
                            value={messageToSend}
                            onChatInputChange={onChatInputChange}
                            onChatKeyDown={onChatKeyDown}
                            placeholder={t('Player.Chat.InputPlaceHolder')}
                            maxLength={MAX_CHAT_MESSAGE_LIMIT}
                        />
                        <ChatMessageButton onClick={sendMessage} disabled={!canSendMessage} />
                    </SendMessageInput>
                </div>
            )}
            {!me?.nickname && (
                <div className="row-start-4 bg-accent-lighter z-20">
                    <UpdateNickname />
                </div>
            )}
        </>
    );
};
